import { apiConfig } from '../config/app'
import { ApiResponse, User, LikedProduct } from '../types/user'

export class UserService {
  // Simple request cache to prevent duplicate calls
  private static requestCache = new Map<string, Promise<any>>()

  private static getAuthHeaders(): HeadersInit {
    const accessToken = localStorage.getItem('accessToken')
    const idToken = localStorage.getItem('idToken')

    const headers: HeadersInit = {
      'Content-Type': 'application/json',
      Accept: 'application/json',
    }

    // Prioritize accessToken over idToken
    if (accessToken && accessToken.trim() !== '') {
      headers['Authorization'] = `Bearer ${accessToken}`
    } else if (idToken && idToken.trim() !== '') {
      headers['Authorization'] = `Bearer ${idToken}`
    }

    return headers
  }

  private static async makeAuthenticatedRequest<T>(
    endpoint: string,
    options: RequestInit = {},
  ): Promise<ApiResponse<T>> {
    // Create a cache key based on endpoint and method
    const cacheKey = `${endpoint}-${options.method || 'GET'}`
    
    // If it's a GET request and we have a cached promise, return it
    if (!options.method || options.method === 'GET') {
      if (this.requestCache.has(cacheKey)) {
        return this.requestCache.get(cacheKey) as Promise<ApiResponse<T>>
      }
    }

    const requestPromise = this.executeRequest<T>(endpoint, options)
    
    // Cache GET requests
    if (!options.method || options.method === 'GET') {
      this.requestCache.set(cacheKey, requestPromise)
      
      // Clear cache after request completes (success or error)
      requestPromise.finally(() => {
        this.requestCache.delete(cacheKey)
      })
    }

    return requestPromise
  }

  private static async executeRequest<T>(
    endpoint: string,
    options: RequestInit = {},
  ): Promise<ApiResponse<T>> {
    try {
      const base = new URL(apiConfig.baseUrl)
      const path = base.pathname.replace(/\/?ecom\/?$/, '/').replace(/\/+$|^\/+/, '/')
      const originRoot = `${base.origin}${path}`.replace(/\/$/, '')
      const url = `${originRoot}${endpoint}`
      const authHeaders = this.getAuthHeaders()


      // Making authenticated request

      const response = await fetch(url, {
        ...options,
        headers: {
          ...authHeaders,
          ...options.headers,
        },
      })

      if (!response.ok) {
        if (response.status === 401) {
          return {
            success: false,
            error: 'Unauthorized - please log in again',
          }
        }

        // Try to get error message from response body
        let errorMessage = ''
        try {
          const contentType = response.headers.get('content-type')
          
          // Check if response is JSON
          if (contentType && contentType.includes('application/json')) {
            const errorData = await response.json()
            errorMessage = errorData.message || errorData.error || ''
          } else {
            // Try to get plain text error message (for any non-JSON response)
            errorMessage = await response.text()
          }
          
          // Remove surrounding quotes if present (in case backend returns quoted string)
          if (errorMessage) {
            errorMessage = errorMessage.trim()
            if ((errorMessage.startsWith('"') && errorMessage.endsWith('"')) ||
                (errorMessage.startsWith("'") && errorMessage.endsWith("'"))) {
              errorMessage = errorMessage.slice(1, -1)
            }
          }
          
          // If we got a meaningful error message from the body, use it
          if (errorMessage && errorMessage.trim()) {
            return {
              success: false,
              error: errorMessage.trim(),
            }
          }
        } catch (e) {
          // If parsing fails, fall through to default error
        }

        // Default error message without status code
        return {
          success: false,
          error: response.statusText || 'Помилка оновлення даних',
        }
      }

      const data = await response.json()

      return {
        success: true,
        data,
      }
    } catch (error) {
      if (error instanceof TypeError && error.message.includes('fetch')) {
        return {
          success: false,
          error: 'Network error: Unable to connect to the server',
        }
      }
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Unknown error occurred',
      }
    }
  }

  // Helper to transform backend user data (name -> firstName/lastName)
  private static transformUserFromBackend(backendUser: any): User {
    const user = { ...backendUser }
    
    if (user.personal && user.personal.name) {
      const nameParts = user.personal.name.trim().split(' ')
      user.personal.firstName = nameParts[0] || null
      user.personal.lastName = nameParts.slice(1).join(' ') || null
      delete user.personal.name
    }
    
    return user as User
  }

  // Get current user profile
  static async getCurrentUser(): Promise<ApiResponse<User>> {
    const response = await this.makeAuthenticatedRequest<any>('/user')
    
    if (response.success && response.data) {
      return {
        success: true,
        data: this.transformUserFromBackend(response.data),
      }
    }
    
    return response
  }

  // Get user profile with detailed information
  // NOTE: Reuses getCurrentUser to avoid duplicate API calls to /user endpoint
  static async getUserProfile(): Promise<ApiResponse<User>> {
    return this.getCurrentUser()
  }

  // Update user profile (personal + contact info)
  static async updateUserProfile(profileData: {
    personal?: {
      firstName?: string
      lastName?: string
      birthDate?: string
    }
    contacts?: {
      phone?: string
      email?: string
    }
  }): Promise<ApiResponse<User>> {
    // Transform the data to concatenate firstName and lastName into name
    const transformedData: any = {}
    
    if (profileData.personal) {
      transformedData.personal = { ...profileData.personal }
      
      // Concatenate firstName and lastName into name field
      if (profileData.personal.firstName || profileData.personal.lastName) {
        const firstName = profileData.personal.firstName || ''
        const lastName = profileData.personal.lastName || ''
        transformedData.personal.name = `${firstName} ${lastName}`.trim()
        
        // Remove firstName and lastName from the payload
        delete transformedData.personal.firstName
        delete transformedData.personal.lastName
      }
    }
    
    // Send contacts update directly - let backend handle validation
    if (profileData.contacts) {
      transformedData.contacts = { ...profileData.contacts }
    }
    
    const response = await this.makeAuthenticatedRequest<any>('/user', {
      method: 'PUT',
      body: JSON.stringify(transformedData),
    })
    
    // Transform the response to split name into firstName/lastName
    if (response.success && response.data) {
      return {
        success: true,
        data: this.transformUserFromBackend(response.data),
      }
    }
    
    return response
  }

  // Get user addresses
  static async getUserAddresses(): Promise<
    ApiResponse<
      Array<{
        id: string
        title: string
        city: string
        street: string
        house: string
        entrance?: string
        apartment?: string
        isDefault: boolean
      }>
    >
  > {
    return this.makeAuthenticatedRequest('/user/addresses')
  }

  // Add new address
  static async addAddress(addressData: {
    title: string
    city: string
    street: string
    house: string
    entrance?: string
    apartment?: string
    isDefault?: boolean
  }): Promise<ApiResponse<{ id: string }>> {
    return this.makeAuthenticatedRequest('/user/addresses', {
      method: 'POST',
      body: JSON.stringify(addressData),
    })
  }

  // Update address
  static async updateAddress(
    addressId: string,
    addressData: {
      title?: string
      city?: string
      street?: string
      house?: string
      entrance?: string
      apartment?: string
      isDefault?: boolean
    },
  ): Promise<ApiResponse<{ id: string }>> {
    return this.makeAuthenticatedRequest(`/user/addresses/${addressId}`, {
      method: 'PUT',
      body: JSON.stringify(addressData),
    })
  }

  // Delete address
  static async deleteAddress(addressId: string): Promise<ApiResponse<void>> {
    return this.makeAuthenticatedRequest(`/user/addresses/${addressId}`, {
      method: 'DELETE',
    })
  }

  // Set default address
  static async setDefaultAddress(addressId: string): Promise<ApiResponse<void>> {
    return this.makeAuthenticatedRequest(`/user/addresses/${addressId}/default`, {
      method: 'PUT',
    })
  }

  // Get user orders
  static async getUserOrders(params?: {
    limit?: number
    offset?: number
    status?: string
  }): Promise<
    ApiResponse<Array<{
      id: string
      orderNumber: string
      customerId: string
      phone: string | null
      email: string
      firstName: string
      lastName: string
      status: string
      statusLabel: string
      deliveryType: string
      deliverySubtype: string | null
      redirectUrl: string | null
      amount: number
      discountAmount: number
      grandTotalAmount: number
      productDiscountAmount?: number
      shippingAddress: {
        id: string
        isPrimary: boolean | null
        region: string
        deliveryType: string | null
        deliverySubtype: string | null
        shippingCity: string
        warehouseId: string | null
        warehouseLabel: string | null
        shippingStreet: string
        shippingHouse: string | null
        shippingEntrance: string | null
        shippingApartment: string | null
        shippingComment: string | null
        createdAt: string
        updatedAt: string
      }
      notesPrivate: string
      createdAt: string
      updatedAt: string
      items: Array<{
        id: string
        productId: string
        title: string
        icon: string
        unitPrice: number
        quantity: number
        totalAmount: number
        basePrice?: number
        discountPercent?: number
        discountPerUnit?: number
        discountAmount?: number
        createdAt: string
        updatedAt: string
      }>
      events: Array<{
        status: string
        statusLabel: string
        message: string
        changedAt: string
      }>
    }>>
  > {
    const queryParams = new URLSearchParams()
    if (params?.limit) queryParams.append('limit', params.limit.toString())
    if (params?.offset) queryParams.append('offset', params.offset.toString())
    if (params?.status) queryParams.append('status', params.status)

    const queryString = queryParams.toString()
    const endpoint = queryString ? `/ecom/user/orders?${queryString}` : '/ecom/user/orders'

    return this.makeAuthenticatedRequest(endpoint)
  }

  // Get order by order number
  static async getOrderByNumber(orderNumber: string): Promise<
    ApiResponse<{
      id: string
      orderNumber: string
      status: string
      totalAmount: number
      currency: string
      createdAt: string
      updatedAt: string
      items: Array<{
        id: string
        name: string
        description: string
        price: number
        currency: string
        quantity: number
        image: string
      }>
      shippingAddress: {
        id: string
        recipientName: string
        phone: string
        city: string
        street: string
        house: string
        apartment?: string
        entrance?: string
        comment?: string
      }
      paymentMethod: string
      deliveryMethod: string
    }>
  > {
    return this.makeAuthenticatedRequest(`/ecom/user/orders/number/${orderNumber}`)
  }

  static async getUserLikedProducts(): Promise<ApiResponse<LikedProduct[]>> {
    return this.makeAuthenticatedRequest<LikedProduct[]>('/user/liked')
  }

  static async addToLiked(productId: string): Promise<ApiResponse<{ id: string }>> {
    return this.makeAuthenticatedRequest('/user/liked', {
      method: 'POST',
      body: JSON.stringify({ productId }),
    })
  }

  static async removeFromLiked(productId: string): Promise<ApiResponse<void>> {
    return this.makeAuthenticatedRequest('/user/liked', {
      method: 'DELETE',
      body: JSON.stringify({ productId }),
    })
  }

  // Confirm email or phone with code
  static async confirmAttribute(code: string, attribute: string): Promise<ApiResponse<User>> {
    const response = await this.makeAuthenticatedRequest<any>('/user/confirm', {
      method: 'PUT',
      body: JSON.stringify({ code, attribute }),
    })
    
    // Transform the response to split name into firstName/lastName
    if (response.success && response.data) {
      return {
        success: true,
        data: this.transformUserFromBackend(response.data),
      }
    }
    
    return response
  }

  // Delete order by ID
  static async deleteOrder(orderId: string): Promise<ApiResponse<void>> {
    return this.makeAuthenticatedRequest(`/user/orders/${orderId}`, {
      method: 'DELETE',
    })
  }

}

export default UserService
